/**
 * bootSession.js
 *
 * The Vue-free decisions made once at boot: whether App.vue's `window.load`
 * handler should tear the session down and bounce to home, and whether
 * main.js starts session recording on this page.
 *
 * It lives in its own module because the handler runs exactly once, at page
 * load, inside `<script setup>` - there is no seam to assert it at, and
 * getting it wrong is invisible until a patient loses an OAuth code. Plain
 * JS rather than TS because App.vue is a legacy JS SFC and the lint
 * resolver only knows `.js` and `.vue`.
 */

/**
 * The public report pages: a patient's private link and the report-ID check.
 * Opened fresh by doctors who never ticked "remember this device", and they
 * show a patient's health data, so the app also keeps session recording off
 * them (main.js).
 */
const PUBLIC_REPORT_PATH_PATTERNS = [
  /^\/r\/[^/]+\/?$/,
  /^\/verify\/[^/]+\/?$/,
];

/** @param {string} pathname */
export function isPublicReportPath(pathname) {
  return PUBLIC_REPORT_PATH_PATTERNS.some((re) => re.test(pathname));
}

/**
 * Paths a fresh navigation lands on that must survive boot untouched. The
 * wearable OAuth callback carries a single-use code and a CSRF state in its
 * query string; the boot handler's `router.push` drops both before
 * WearableCallback.vue can read them, and its `logout()` clears the clinician
 * session that view's non-mobile branch needs. Vendor redirects are never
 * reloads and a patient's phone browser has never ticked "remember this
 * device", so without this exemption every connect ended on /login. The
 * public report pages would be sent to /login the same way.
 */
const BOOT_SAFE_PATH_PATTERNS = [
  /^\/wearables\/[^/]+\/callback$/,
  ...PUBLIC_REPORT_PATH_PATTERNS,
];

/** @param {string} pathname */
export function isBootSafePath(pathname) {
  return BOOT_SAFE_PATH_PATTERNS.some((re) => re.test(pathname));
}

/**
 * True when this load should log the user out and send them home. The
 * product rule is "a device that never ticked remember-me keeps no session
 * across a tab close" - a reload is not a tab close, and a boot-safe path
 * is not ours to interrupt.
 *
 * @param {{ rememberMe: boolean, isReload: boolean, pathname: string }} input
 * @returns {boolean}
 */
export function shouldClearSessionOnBoot({ rememberMe, isReload, pathname }) {
  if (isBootSafePath(pathname)) return false;
  return !rememberMe && !isReload;
}
